import React, { FC } from 'react'
import Head from 'react-helmet'
import SearchAutocomplete from '../../Search/SearchAutocomplete'
import { Department } from '@/domain/department'
interface PageContextProps {
  refinementList?: Record<string, unknown>
  menu?: Record<string, unknown>
}

export interface HomePageHeroProps {
  department?: Department
  pageContext: PageContextProps
}

const HomePageHero: FC<HomePageHeroProps> = ({ department, pageContext }) => {
  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          href="/images/recovery-home-poster.jpg"
        />
      </Head>
      <div className="relative w-full select-none">
        <div className="hidden md:flex ">
          <DesktopHero
            department={department}
            pageContext={pageContext}
          />
        </div>
        <div className="md:hidden">
          <MobileHero
            department={department}
            pageContext={pageContext}
          />
        </div>
      </div>
    </>
  )
}
export default HomePageHero

const blurContainerWidth = {
  default: 'max-w-[464px]',
  providers: 'max-w-[540px]'
}

const DesktopHero: FC<HomePageHeroProps> = ({
  pageContext,
  department = Department.Default
}) => {
  const { refinementList, menu } = pageContext || {}
  const videoSrc =
    'https://res.cloudinary.com/rehabpath/video/upload/c_fill,w_1512,ar_16:9/v1706031321/assets/recovery_home_hero.mp4'

  return (
    <>
      <div className="absolute left-0 top-0 size-full bg-[radial-gradient(289.99%_94.23%_at_2.04%_22.21%,rgba(18,10,68,0.5)_0%,rgba(74,60,157,0.5)_99.99%,rgba(74,60,157,0.5)_100%)]" />
      <div className="absolute left-0 top-0 size-full bg-gray-1100/50" />

      <div className="content absolute z-20 m-0 flex size-full flex-col items-center justify-center px-8 py-0">
        <div className={`mx-auto ${blurContainerWidth[department]}`}>
          <HeroTitle department={department} />
          <SearchAutocomplete
            referralParamValue="searchboxHero"
            className="mt-8 hidden md:block"
            id="home-autocomplete"
            pageContext={pageContext}
            refinementList={{
              ...menu,
              ...refinementList
            }}
            isHomePage={true}
          />
        </div>
      </div>
      <div className="h-[65vh] w-full overflow-hidden md:max-h-[400px] xl:max-h-[650px]">
        {department === Department.Default ? (
          <video
            className="block size-full object-cover"
            muted={true}
            autoPlay
            playsInline
            loop
            poster={'/images/recovery-home-poster.jpg'}
            src={videoSrc}
          />
        ) : (
          <img
            src="/images/hero-pro.webp"
            alt="Recovery Home Hero"
            className="relative z-[-1] block md:top-[-70px] lg:top-[-270px] xl:w-full"
          />
        )}
      </div>
    </>
  )
}

const MobileHero: FC<HomePageHeroProps> = ({
  pageContext,
  department = Department.Default
}) => {
  const { refinementList, menu } = pageContext || {}
  const videoSrc =
    'https://res.cloudinary.com/rehabpath/video/upload/c_fill,w_1512,ar_16:9/v1706031321/assets/recovery_home_hero.mp4'

  return (
    <div>
      <div className="relative mx-4 mt-3">
        <div className="absolute left-0 top-0 size-full rounded-2xl bg-[linear-gradient(180deg,rgba(0,0,0,0.00)_39.89%,#000_100%)]" />
        <div className="absolute left-0 top-0 size-full rounded-2xl bg-[linear-gradient(199deg,rgba(59,47,147,0.29)_43.83%,rgba(34,27,89,0.80)_86.21%)]" />

        <div className="content absolute z-20 flex size-full flex-col items-start justify-end gap-7 rounded-2xl px-6 py-8">
          <HeroTitle department={department} />
          <SearchAutocomplete
            referralParamValue="searchboxHero"
            className="block w-full text-sm"
            id="mobile-hero-autocomplete"
            pageContext={pageContext}
            refinementList={{
              ...menu,
              ...refinementList
            }}
            isHomePage={true}
          />
        </div>
        <div className="h-[448px] w-full overflow-hidden">
          {department === Department.Default ? (
            <video
              className="block size-full rounded-2xl object-cover"
              muted={true}
              autoPlay
              playsInline
              loop
              poster={'/images/recovery-home-poster.jpg'}
              src={videoSrc}
            />
          ) : (
            <img
              className="block size-full rounded-2xl object-cover"
              src="/images/hero-pro.webp"
              alt="Recovery Home Hero"
            />
          )}
        </div>
      </div>
    </div>
  )
}

const HeroTitleProviders: FC = () => {
  return (
    <div className="whitespace-break-spaces text-pretty pr-4 text-left text-[26px] font-light leading-8 text-primary-25 sm:pr-0 md:text-[32px] md:leading-[38px] md:text-white">
      Confidently make effective <br className="hidden md:block" />
      behavioral health referrals
    </div>
  )
}

const HeroTitleDefault: FC = () => {
  return (
    <div className="text-pretty pr-4 text-left text-[26px] font-light leading-8 text-primary-25 sm:pr-0 md:text-[32px] md:leading-[38px] md:text-white">
      Recovery starts today.
      <br /> Find{' '}
      <span className="font-FieldsVariable font-medium">
        the best treatment{' '}
      </span>
      for you.
    </div>
  )
}

const HeroTitle: FC<{ department?: Department }> = ({ department }) => {
  switch (department) {
    case Department.Providers:
      return <HeroTitleProviders />
    default:
      return <HeroTitleDefault />
  }
}
